import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import { useTimeZone } from '../../../../../hooks'
import { getListingRentDisplayValue } from '../../../../RentalPortalListing'
import { RentalPortalPhaseBar } from '../../../RentalPortalPhaseBar'
import { useListingProfileContext } from '../RentalPortalListingProfile.hooks'

import {
  LISTING_PROFILE_AVAILABLE_FROM_ACTION_KEY_DELETE,
  ListingProfileAvailableFromItem,
} from './ListingProfileAvailableFromItem'
import { ListingProfileHeaderInfoItemsSkeleton } from './ListingProfileHeaderInfoItemsSkeleton'
import { ListingProfileRentItem } from './ListingProfileRentItem'
import { ListingProfileResponsibleManagerItem } from './ListingProfileResponsibleManagerItem'
import {
  LISTING_PROFILE_HEADER_INFO_ITEMS_COLUMNS,
  LISTING_PROFILE_INFO_ITEM_TITLE_KEY,
  QA_NAME_LISTING_PROFILE_PHASE_BAR,
} from './RentalPortalListingProfileHeaderInfoItems.constants'
import { useListingProfileHeaderInfoItems } from './RentalPortalListingProfileHeaderInfoItems.hooks'
import {
  getCanEditListingAvailableFrom,
  getCanEditListingResponsibleManager,
  getListingPhaseItems,
  getListingPhaseTerminalDate,
  getListingPhaseTerminalStatement,
  isListingInactive,
} from './RentalPortalListingProfileHeaderInfoItems.utils'

import * as styles from './RentalPortalListingProfileHeaderInfoItems.module.css'

function RentalPortalListingProfileHeaderInfoItems({ isLoading }) {
  const { t } = useTranslation()

  const timezone = useTimeZone()

  const { isPreview, listingData } = useListingProfileContext()

  const { handleOpenResponsibleManagerModal, handleSaveAvailableFrom } =
    useListingProfileHeaderInfoItems()

  const isTerminal = Boolean(listingData?.phase?.is_terminal)

  const phaseItems = getListingPhaseItems({
    phases: listingData?.phases,
    status: listingData?.status,
    t,
  })

  const canEditAvailableFrom = getCanEditListingAvailableFrom({
    isPreview,
    listingData,
  })

  const canEditResponsibleManager = getCanEditListingResponsibleManager({
    isPreview,
    listingData,
  })

  const availableFrom = listingData?.available_from
  const rentValue = listingData ? getListingRentDisplayValue(listingData) : null
  const responsibleManager = listingData?.responsible_user

  return (
    <div className={styles.RentalPortalListingProfileHeaderInfoItems}>
      <RentalPortalPhaseBar
        isLoading={isLoading}
        items={phaseItems}
        qaName={QA_NAME_LISTING_PROFILE_PHASE_BAR}
        terminalStatement={
          isTerminal
            ? getListingPhaseTerminalStatement({
                phase: listingData?.phase,
                t,
                terminalDate: getListingPhaseTerminalDate(listingData),
                timezone,
              })
            : null
        }
      />
      <div
        className={styles.infoGrid}
        style={{
          '--listing-profile-header-info-items-columns':
            LISTING_PROFILE_HEADER_INFO_ITEMS_COLUMNS,
        }}
      >
        {isLoading ? (
          <ListingProfileHeaderInfoItemsSkeleton className={styles.infoItem} />
        ) : (
          <>
            <ListingProfileRentItem
              className={styles.infoItem}
              contentWrapperClassName={styles.contentWrapper}
              titleClassName={styles.infoItemTitle}
              value={rentValue}
            />
            <ListingProfileAvailableFromItem
              canEdit={canEditAvailableFrom}
              className={styles.infoItem}
              contentWrapperClassName={classNames(styles.contentWrapper, {
                [styles.editableContentWrapper]: canEditAvailableFrom,
              })}
              isInactive={isListingInactive(listingData)}
              itemActionsProps={{
                actionsWrapperClassName: styles.managerActions,
                buttonsProps: {
                  [LISTING_PROFILE_AVAILABLE_FROM_ACTION_KEY_DELETE]: {
                    className: styles.managerActionButton,
                  },
                },
              }}
              title={LISTING_PROFILE_INFO_ITEM_TITLE_KEY.availableFrom}
              titleClassName={styles.infoItemTitle}
              value={availableFrom}
              onSaveValue={handleSaveAvailableFrom}
            />
            <ListingProfileResponsibleManagerItem
              actionButtonClassName={styles.managerActionButton}
              actionsWrapperClassName={styles.managerActions}
              canEdit={canEditResponsibleManager}
              className={styles.infoItem}
              contentWrapperClassName={classNames(styles.contentWrapper, {
                [styles.editableContentWrapper]: canEditResponsibleManager,
              })}
              managerClassName={styles.managerItem}
              titleClassName={styles.infoItemTitle}
              value={responsibleManager}
              onOpenModal={handleOpenResponsibleManagerModal}
            />
          </>
        )}
      </div>
    </div>
  )
}

export default RentalPortalListingProfileHeaderInfoItems
